import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import './AdminPageBuilder.css';
import LoadingSpinner from '../components/LoadingSpinner';
import ImageUploader from '../components/ImageUploader';

const AdminPageBuilder = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Extract the dynamic page path from the URL
    // e.g. /admin-page-builder/nuova-pagina -> /nuova-pagina
    const targetPath = location.pathname.replace('/admin-page-builder', '') || '/';
    const docId = targetPath.startsWith('/') ? targetPath.substring(1) : targetPath;

    const [pageData, setPageData] = useState({ title: '', blocks: [] });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const loadPageData = async () => {
            if (!currentUser) {
                navigate('/admin-login-segreto-xyz123');
                return;
            }
            if (!docId) {
                setError("Nessun percorso pagina specificato.");
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const pageRef = doc(db, 'dynamic_pages', docId);
                const docSnap = await getDoc(pageRef);
                if (docSnap.exists()) {
                    setPageData(docSnap.data());
                } else {
                    // Initialize empty page if it doesn't exist
                    setPageData({
                        path: targetPath,
                        title: 'Nuova Pagina',
                        blocks: []
                    });
                }
            } catch (err) {
                console.error("Errore caricamento pagina:", err);
                setError("Errore nel caricamento della pagina.");
            } finally {
                setLoading(false);
            }
        };
        loadPageData();
    }, [currentUser, navigate, docId, targetPath]);

    const handleSave = async () => {
        setSaving(true);
        setError(''); setSuccess('');
        try {
            const pageRef = doc(db, 'dynamic_pages', docId);
            await setDoc(pageRef, {
                ...pageData,
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.email
            }, { merge: true });
            setSuccess("Pagina salvata con successo!");
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error("Errore salvataggio pagina:", err);
            setError("Errore durante il salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    const addBlock = (type) => {
        const newBlock = {
            id: Date.now().toString(),
            type,
            align: 'left',
            content: '',
            url: '',
            buttonLink: '',
            buttonLabel: '',
            slides: [], // For slider
            posts: [] // For post-grid
        };
        setPageData(prev => ({ ...prev, blocks: [...prev.blocks, newBlock] }));
    };

    const updateBlock = (id, field, value) => {
        setPageData(prev => ({
            ...prev,
            blocks: prev.blocks.map(b => b.id === id ? { ...b, [field]: value } : b)
        }));
    };

    const removeBlock = (id) => {
        setPageData(prev => ({
            ...prev,
            blocks: prev.blocks.filter(b => b.id !== id)
        }));
    };

    const moveBlock = (index, direction) => {
        if ((direction === -1 && index === 0) || (direction === 1 && index === pageData.blocks.length - 1)) return;
        const newBlocks = [...pageData.blocks];
        const temp = newBlocks[index];
        newBlocks[index] = newBlocks[index + direction];
        newBlocks[index + direction] = temp;
        setPageData(prev => ({ ...prev, blocks: newBlocks }));
    };

    const addSlide = (blockId) => {
        setPageData(prev => ({
            ...prev,
            blocks: prev.blocks.map(b => {
                if (b.id === blockId) {
                    return { ...b, slides: [...(b.slides || []), { url: '', caption: '' }] };
                }
                return b;
            })
        }));
    };

    const updateSlide = (blockId, slideIndex, field, value) => {
        setPageData(prev => ({
            ...prev,
            blocks: prev.blocks.map(b => {
                if (b.id === blockId) {
                    const newSlides = [...b.slides];
                    newSlides[slideIndex] = { ...newSlides[slideIndex], [field]: value };
                    return { ...b, slides: newSlides };
                }
                return b;
            })
        }));
    };

    const removeSlide = (blockId, slideIndex) => {
        setPageData(prev => ({
            ...prev,
            blocks: prev.blocks.map(b => {
                if (b.id === blockId) {
                    return { ...b, slides: b.slides.filter((_, i) => i !== slideIndex) };
                }
                return b;
            })
        }));
    };

    const addPost = (blockId, type) => {
        setPageData(prev => ({
            ...prev,
            blocks: prev.blocks.map(b => {
                if (b.id === blockId) {
                    const newPost = type === 'facebook' 
                        ? { type: 'facebook', embedCode: '' }
                        : { type: 'internal', image: '', title: '', text: '', buttonLabel: '', buttonLink: '' };
                    // Add to the beginning so newest is on top-left (assuming we unshift)
                    // The prompt said "li aggiunga dall'alto, devono apparire 2 colonne, il piu recente a sinistra...".
                    // This means unshift is best.
                    return { ...b, posts: [newPost, ...(b.posts || [])] };
                }
                return b;
            })
        }));
    };

    const updatePost = (blockId, postIndex, field, value) => {
        setPageData(prev => ({
            ...prev,
            blocks: prev.blocks.map(b => {
                if (b.id === blockId) {
                    const newPosts = [...b.posts];
                    newPosts[postIndex] = { ...newPosts[postIndex], [field]: value };
                    return { ...b, posts: newPosts };
                }
                return b;
            })
        }));
    };

    const removePost = (blockId, postIndex) => {
        setPageData(prev => ({
            ...prev,
            blocks: prev.blocks.map(b => {
                if (b.id === blockId) {
                    return { ...b, posts: b.posts.filter((_, i) => i !== postIndex) };
                }
                return b;
            })
        }));
    };

    if (loading) return <LoadingSpinner show={true} />;

    if (error && !saving) return (
        <div className="admin-page-builder container">
            <h1 style={{marginTop: '40px'}}>Errore</h1>
            <p className="error">{error}</p>
        </div>
    );

    return (
        <div className="admin-page-builder">
            <div className="builder-header">
                <div className="container header-flex">
                    <div>
                        <h1>Costruttore Pagina</h1>
                        <p className="page-path-display">Modifica: <strong>{targetPath}</strong></p>
                    </div>
                    <div className="header-actions">
                        <button className="preview-btn" onClick={() => window.open(targetPath, '_blank')}>👁️ Anteprima</button>
                        <button className="cta-button" onClick={handleSave} disabled={saving}>
                            {saving ? 'Salvataggio...' : '💾 Salva Pagina'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="container builder-layout">
                <div className="blocks-sidebar">
                    <h3>Aggiungi Blocco</h3>
                    <button onClick={() => addBlock('h1')}>Titolo Principale (H1)</button>
                    <button onClick={() => addBlock('h2')}>Sottotitolo (H2)</button>
                    <button onClick={() => addBlock('p')}>Paragrafo</button>
                    <button onClick={() => addBlock('image')}>Immagine Singola</button>
                    <button onClick={() => addBlock('button')}>Pulsante</button>
                    <button onClick={() => addBlock('slider')}>Slider Immagini</button>
                    <button onClick={() => addBlock('post-grid')} style={{backgroundColor: '#e0c097', color: '#333'}}>FB / Griglia Post</button>
                    <button onClick={() => addBlock('js')} className="warning-btn">Integrazione Esterna (JS/HTML)</button>
                </div>

                <div className="blocks-editor-area">
                    {success && <div className="form-status success">{success}</div>}
                    
                    <div className="global-page-settings">
                        <label>Titolo Pagina (per tab e SEO)</label>
                        <input 
                            type="text" 
                            value={pageData.title || ''} 
                            onChange={(e) => setPageData(p => ({...p, title: e.target.value}))} 
                            placeholder="Es. Nuova Pagina"
                        />
                    </div>

                    {pageData.blocks.length === 0 ? (
                        <div className="empty-state">
                            Nessun blocco presente. Aggiungi il tuo primo blocco dalla barra laterale.
                        </div>
                    ) : (
                        <div className="blocks-list">
                            {pageData.blocks.map((block, index) => (
                                <div key={block.id} className="block-editor-card">
                                    <div className="block-editor-header">
                                        <div className="block-type-badge">{block.type.toUpperCase()}</div>
                                        <div className="block-controls">
                                            <div className="alignment-controls">
                                                <button className={`align-btn ${block.align === 'left' ? 'active' : ''}`} onClick={() => updateBlock(block.id, 'align', 'left')} title="Allinea a Sinistra">⬅️</button>
                                                <button className={`align-btn ${block.align === 'center' ? 'active' : ''}`} onClick={() => updateBlock(block.id, 'align', 'center')} title="Centra">↔️</button>
                                                <button className={`align-btn ${block.align === 'right' ? 'active' : ''}`} onClick={() => updateBlock(block.id, 'align', 'right')} title="Allinea a Destra">➡️</button>
                                            </div>
                                            <button onClick={() => moveBlock(index, -1)} disabled={index === 0} title="Sposta su">⬆️</button>
                                            <button onClick={() => moveBlock(index, 1)} disabled={index === pageData.blocks.length - 1} title="Sposta giù">⬇️</button>
                                            <button className="delete-btn" onClick={() => removeBlock(block.id)} title="Elimina blocco">🗑️</button>
                                        </div>
                                    </div>

                                    <div className="block-editor-content">
                                        {(block.type === 'h1' || block.type === 'h2' || block.type === 'p') && (
                                            <textarea 
                                                className="full-width-input"
                                                value={block.content || ''} 
                                                onChange={(e) => updateBlock(block.id, 'content', e.target.value)} 
                                                placeholder={`Inserisci testo per ${block.type.toUpperCase()}`}
                                                rows={block.type === 'p' ? 5 : 2}
                                            />
                                        )}
                                        
                                        {block.type === 'image' && (
                                            <input 
                                                type="text" 
                                                className="full-width-input"
                                                value={block.url || ''} 
                                                onChange={(e) => updateBlock(block.id, 'url', e.target.value)} 
                                                placeholder="URL Immagine (es. https://... o /images/...)"
                                            />
                                        )}

                                        {block.type === 'button' && (
                                            <div className="button-inputs">
                                                <input 
                                                    type="text" 
                                                    value={block.buttonLabel || ''} 
                                                    onChange={(e) => updateBlock(block.id, 'buttonLabel', e.target.value)} 
                                                    placeholder="Testo Pulsante"
                                                />
                                                <input 
                                                    type="text" 
                                                    value={block.buttonLink || ''} 
                                                    onChange={(e) => updateBlock(block.id, 'buttonLink', e.target.value)} 
                                                    placeholder="Link (es. /contatti)"
                                                />
                                            </div>
                                        )}

                                        {block.type === 'js' && (
                                            <div>
                                                <p className="warning-text">⚠️ Attenzione: Inserisci solo codice fidato. Il codice HTML/JS verrà eseguito sulla pagina.</p>
                                                <textarea 
                                                    className="full-width-input code-input"
                                                    value={block.content || ''} 
                                                    onChange={(e) => updateBlock(block.id, 'content', e.target.value)} 
                                                    placeholder="<!-- Inserisci HTML / Scripts qui -->"
                                                    rows={8}
                                                />
                                            </div>
                                        )}

                                        {block.type === 'slider' && (
                                            <div className="slider-editor">
                                                {(block.slides || []).map((slide, sIndex) => (
                                                    <div key={sIndex} className="slide-input-row">
                                                        <span className="slide-num">#{sIndex + 1}</span>
                                                        <input 
                                                            type="text" 
                                                            value={slide.url || ''} 
                                                            onChange={(e) => updateSlide(block.id, sIndex, 'url', e.target.value)} 
                                                            placeholder="URL Immagine"
                                                        />
                                                        <input 
                                                            type="text" 
                                                            value={slide.caption || ''} 
                                                            onChange={(e) => updateSlide(block.id, sIndex, 'caption', e.target.value)} 
                                                            placeholder="Didascalia (opzionale)"
                                                        />
                                                        <button className="remove-slide-btn" onClick={() => removeSlide(block.id, sIndex)}>✖</button>
                                                    </div>
                                                ))}
                                                <button className="add-slide-btn" onClick={() => addSlide(block.id)}>+ Aggiungi Immagine allo Slider</button>
                                            </div>
                                        )}

                                        {block.type === 'post-grid' && (
                                            <div className="post-grid-editor">
                                                <div className="add-post-actions" style={{marginBottom: '15px', display: 'flex', gap: '10px'}}>
                                                    <button className="add-slide-btn" onClick={() => addPost(block.id, 'facebook')}>+ Aggiungi Post Facebook</button>
                                                    <button className="add-slide-btn" onClick={() => addPost(block.id, 'internal')}>+ Aggiungi Post Interno</button>
                                                </div>
                                                <div className="posts-editor-list" style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                                                    {(block.posts || []).map((post, pIndex) => (
                                                        <div key={pIndex} className="post-input-card" style={{border: '1px solid #ddd', padding: '15px', borderRadius: '5px', position: 'relative'}}>
                                                            <button className="remove-slide-btn" style={{position: 'absolute', top: '10px', right: '10px'}} onClick={() => removePost(block.id, pIndex)}>✖</button>
                                                            {post.type === 'facebook' ? (
                                                                <>
                                                                    <strong>Post Facebook (Codice Incorpora)</strong>
                                                                    <textarea 
                                                                        className="full-width-input"
                                                                        value={post.embedCode !== undefined ? post.embedCode : (post.url || '')} 
                                                                        onChange={(e) => updatePost(block.id, pIndex, 'embedCode', e.target.value)} 
                                                                        placeholder='Incolla qui il codice "Incorpora" o il link del post Facebook'
                                                                        rows={4}
                                                                        style={{marginTop: '10px'}}
                                                                    />
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <strong>Post Interno</strong>
                                                                    <div style={{marginTop: '10px', marginBottom: '10px'}}>
                                                                        <ImageUploader 
                                                                            currentImageUrl={post.image || ''}
                                                                            onUploadComplete={(url) => updatePost(block.id, pIndex, 'image', url)}
                                                                            folderPath="post_images"
                                                                        />
                                                                    </div>
                                                                    <input 
                                                                        type="text" 
                                                                        className="full-width-input"
                                                                        value={post.title || ''} 
                                                                        onChange={(e) => updatePost(block.id, pIndex, 'title', e.target.value)} 
                                                                        placeholder="Titolo"
                                                                        style={{marginTop: '10px'}}
                                                                    />
                                                                    <textarea 
                                                                        className="full-width-input"
                                                                        value={post.text || ''} 
                                                                        onChange={(e) => updatePost(block.id, pIndex, 'text', e.target.value)} 
                                                                        placeholder="Testo del post"
                                                                        rows={3}
                                                                        style={{marginTop: '10px'}}
                                                                    />
                                                                    <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                                                                        <input 
                                                                            type="text" 
                                                                            style={{flex: 1}}
                                                                            value={post.buttonLabel || ''} 
                                                                            onChange={(e) => updatePost(block.id, pIndex, 'buttonLabel', e.target.value)} 
                                                                            placeholder="Testo Pulsante"
                                                                        />
                                                                        <input 
                                                                            type="text" 
                                                                            style={{flex: 1}}
                                                                            value={post.buttonLink || ''} 
                                                                            onChange={(e) => updatePost(block.id, pIndex, 'buttonLink', e.target.value)} 
                                                                            placeholder="Link Pulsante (es. https://...)"
                                                                        />
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminPageBuilder;
